import React, { FunctionComponent, useState } from 'react'
import { Avatar } from '@afterpaytouch/core'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import {
  useAuthentication,
  useConsumerName,
  useConsumerSelector,
  useInitialData,
  useIsPersistentAndOptedIn,
  useIsRecognizedViaPersistentLogin,
  useIsTrustedCheckout,
  useReadyFeatureFlag,
} from '../../state'
import { useRoutePageName } from '../../config/router'
import { Features } from '../../model'
import { isAfterpayInAppBrowser, isCashAppInAppBrowser } from '../../utils/device'
import { useTranslation } from '../../utils/convergence'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { AccountModal } from '../AccountModal'
import styles from './style.module.css'

export const useShouldShowProfileAvatarChip = (): boolean => {
  const isPersistentAndOptedIn = useIsPersistentAndOptedIn()
  const isRecognizedViaPersistentLogin = useIsRecognizedViaPersistentLogin()
  const isTrustedCheckout = useIsTrustedCheckout()
  const isLongLivedSessionEnabled = useReadyFeatureFlag<boolean>(Features.CHECKOUT_LONG_LIVE_SESSION_ENABLED, false)
  const { uuid } = useConsumerSelector()
  const { isIframe } = useInitialData()
  const { verificationRequired } = useAuthentication()
  const isArmSurface = !isIframe && !isAfterpayInAppBrowser() && !isCashAppInAppBrowser()
  return (
    isPersistentAndOptedIn &&
    isRecognizedViaPersistentLogin &&
    !isTrustedCheckout &&
    isLongLivedSessionEnabled &&
    isArmSurface &&
    Boolean(uuid) &&
    !verificationRequired
  )
}

export const ProfileAvatarChip: FunctionComponent = () => {
  const { t } = useTranslation()
  const consumerName = useConsumerName()
  const shouldShow = useShouldShowProfileAvatarChip()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const pageName = useRoutePageName()
  const [showModal, setShowModal] = useState(false)

  if (!shouldShow) {
    return null
  }

  const onAvatarClick = (): void => {
    logEvent(TrackingEvent.PRESSED_AUTO_REMEMBER_ME_PROFILE_AVATAR, { pageName })
    setShowModal(true)
  }

  return (
    <>
      <MaskedElement actionName='Auto Remember Me Profile Avatar'>
        <button
          type='button'
          className={styles.chip}
          onClick={onAvatarClick}
          aria-label={t('common:rememberMeOpenAccountMenu', { name: consumerName })}
          data-testid='auto-remember-me-profile-avatar'
        >
          <Avatar size='S' src='' alt={consumerName} name={consumerName} title={consumerName} />
        </button>
      </MaskedElement>
      <AccountModal show={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}
